import { useState, useEffect } from 'react';

const STORAGE_KEY = 'prism.cookie-consent.v1';

/**
 * Single-button cookie acceptance banner. Used by both /dashboard and /portal
 * shells. Persists acceptance in localStorage so users see it once.
 *
 * Prism only sets functional cookies (HMAC-signed session); no tracking,
 * no third-party. The banner exists for transparency + GDPR Art 7 evidence.
 */
export function CookieBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!window.localStorage.getItem(STORAGE_KEY)) {
      setVisible(true);
    }
  }, []);

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-label="Cookie notice"
      className="fixed inset-x-0 bottom-0 z-[var(--z-overlay)] border-t border-[var(--color-rule)] bg-[var(--color-surface)] shadow-[0_-4px_24px_-8px_rgba(25,54,97,0.12)]"
    >
      <div className="mx-auto flex max-w-[1080px] flex-col items-start gap-4 px-6 py-4 sm:flex-row sm:items-center sm:justify-between sm:gap-6 sm:py-3">
        <p className="font-sans text-[13.5px] leading-relaxed text-[var(--color-ink-muted)]">
          Prism uses functional cookies only — they keep you signed in and
          remember your TOTP device. We don&apos;t set tracking, advertising,
          or third-party cookies. See our{' '}
          <a
            href="/portal/privacy"
            className="font-medium text-[var(--color-primary)] underline-offset-4 hover:underline"
          >
            privacy policy
          </a>{' '}
          for details.
        </p>
        <button
          type="button"
          onClick={() => {
            window.localStorage.setItem(STORAGE_KEY, '1');
            setVisible(false);
          }}
          className="shrink-0 cursor-pointer rounded-[4px] border border-[var(--color-primary)] bg-[var(--color-primary)] px-5 py-2.5 font-sans text-[13.5px] font-semibold text-white transition-colors hover:bg-[var(--color-primary-hover)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-primary)]"
        >
          Accept
        </button>
      </div>
    </div>
  );
}
